import axios from "axios";

/**
 * Proposal generator API client.
 *
 * `preview` is the workhorse: every change on the offer screen re-runs it, so
 * the figures on screen are always priced against the CURRENT rate card for
 * the ACTUAL sites in the list. Nothing is cached client-side — a stale rate
 * shown in a preview is a rate that ends up in a sent proposal.
 */

export type CustomerType =
  | "multi_location_brand"
  | "existing_client_portfolio"
  | "single_location"
  | "completed_site";

export type SectionId =
  | "hero"
  | "admin_time_value"
  | "program_value"
  | "savings_ladder"
  | "regional_pricing"
  | "regional_map"
  | "digouts"
  | "execution_speed"
  | "emergency_response"
  | "scope_note"
  | "hidden_cost"
  | "benefits"
  | "cta"
  | "footer"
  // Maintenance-plan sections — single_location and completed_site only.
  | "site_history"
  | "why_maintenance"
  | "annual_plan"
  | "compliance"
  | "how_it_works"
  | "plan_benefits"
  | "every_location";

export interface SiteInput {
  ref: string;
  name?: string;
  state?: string;
  latitude: number;
  longitude: number;
  yards?: number;
  /** Display only — lets one row say "5 projects, last 2025" instead of five rows. */
  projectCount?: number;
  lastServiced?: string;
}

export interface ParsedRow {
  name: string;
  city: string;
  state: string;
  latitude: number | null;
  longitude: number | null;
  yards: number | null;
  flags: Array<"no_name" | "no_state" | "duplicate_coords" | "needs_geocode" | "geocode_failed">;
  sourceRow: number;
  address?: string;
  zip?: string;
  /** Other names on the list at this same street address, merged in. */
  alsoKnownAs?: string[];
}

/** One address-level merge the parser made. */
export interface AddressDuplicate {
  address: string;
  city: string;
  state: string;
  kept: { name: string; sourceRow: number };
  dropped: Array<{ name: string; sourceRow: number }>;
}

export interface ParseSummary {
  total: number;
  withCoordinates: number;
  needGeocode: number;
  withYards: number;
  flagged: number;
  /** Rows in the file before address duplicates were merged. */
  read?: number;
  duplicatesMerged?: number;
}

export interface CommitmentLevel {
  locations: number;
  discount: number;
  digOuts: boolean;
  label: string;
}

export interface ProposalRegion {
  key: string;
  name: string;
  stateCodes: string[];
  stateNames: string[];
  locationCount: number;
  pricedLocations: number;
  baseRate: number;
  minRate: number;
  maxRate: number;
  avgYards: number;
  yardsKnownFor: number;
  color: string;
}

export interface SingleSiteFacts {
  name: string;
  city: string | null;
  state: string | null;
  pricePerYard: number;
  originalYards: number | null;
  topUpYards: number;
  annualCost: number;
  serviced: boolean;
  lastServicedOn: string | null;
}

export type OfferType = "commitment_program" | "priced_list" | "single_job" | "top_up" | "maintenance_plan";
export type FacilityType =
  | "childcare"
  | "k12"
  | "parks_municipal"
  | "hoa_multifamily"
  | "church_camp"
  | "other"
  | "developer_playground"
  | "developer_generic"
  | "non_playground";
export type AssumptionSource = "measured" | "list" | "default" | "operator";

export interface AssumptionDef {
  key: string;
  label: string;
  unit?: string;
  kind: "number" | "text" | "choice";
  choices?: Array<{ value: string; label: string }>;
  requiresReason?: boolean;
}

export interface AssumptionValue {
  key: string;
  value: number | string;
  source: AssumptionSource;
  reason?: string;
  detail?: string;
}

export interface ProposalPayload {
  customerType: CustomerType;
  offerType?: OfferType;
  facilityType?: FacilityType;
  assumptions?: Record<string, AssumptionValue>;
  prospectName: string;
  headline: [string, string];
  tagline: string;
  documentTitle: string;
  portfolio: {
    totalLocations: number;
    stateCount: number;
    completedLocations?: number;
    avgYardsPerLocation: number;
    stateLocationCounts: Record<string, number>;
  };
  regions: ProposalRegion[];
  /** Present only for maintenance-plan proposals. */
  singleSite?: SingleSiteFacts;
  ladder: { enabled: boolean; levels: CommitmentLevel[]; openOnIndex: number };
  sections: Record<SectionId, boolean>;
  [key: string]: any;
}

export interface PreviewWarnings {
  unpricedCount: number;
  unpriced: Array<{ name: string; state: string | null; reason: string }>;
  pricedOnRepairedCoordinates: number;
}

export interface BuildInput {
  customerType: CustomerType;
  prospectName: string;
  name?: string;
  clientId?: string | null;
  productId: string;
  serviceId: string;
  sites: SiteInput[];
  sections?: Partial<Record<SectionId, boolean>>;
  offerType?: OfferType;
  facilityType?: FacilityType;
  /** Operator-set assumptions by key. A measured figure needs a reason. */
  assumptions?: Record<string, { value: number | string; reason?: string }>;
  senderUserId?: string | null;
  prospectLogoUrl?: string | null;
  /** Set when the wizard was opened from a maintenance opportunity. */
  maintenanceOpportunityId?: string | null;
  maintenanceOpportunityIds?: string[] | null;
  ladderLevelCount?: number;
  ladderEnabled?: boolean;
  ladderLevels?: CommitmentLevel[];
  emergencyCase?: "hurricane" | "freeze" | "flood" | "storm" | null;
  headline?: [string, string];
  tagline?: string;
}

/**
 * NestJS returns validation failures as `message` and guard rejections as
 * `error` ("Insufficient permissions"). Reading only `message` turned every
 * 403 into a generic "failed", which is how a permission problem reads as a
 * mystery instead of an answer.
 */
const fail = (e: any, what: string) => {
  const data = e?.response?.data;
  const detail = data?.message || data?.error;
  if (Array.isArray(detail)) return { error: detail.join(", ") };
  if (detail) return { error: String(detail) };
  if (e?.response?.status) return { error: `${what} failed (${e.response.status}).` };
  return { error: `${what} failed.` };
};

export interface ProposalEvent {
  at: string;
  kind: "created" | "link" | "sent" | "resent" | "opened" | "replied" | "regenerated" | "status" | "password";
  summary: string;
  detail: string | null;
  /** The email body the operator wrote, on delivery rows (logged from 2026-09-28). */
  message?: string | null;
  actor: string | null;
}

/** Everything that has happened to this proposal, newest first. */
export const getProposalTimeline = async (
  id: string
): Promise<{ events: ProposalEvent[] } | { error: string }> => {
  try {
    const res = await axios.get(`/proposals/${id}/timeline`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading activity");
  }
};

/** Send the same frozen document to another recipient. Same link, same token. */
export const resendProposal = async (
  id: string,
  input: { to: string; cc?: string[]; subject?: string; message?: string; includePassword?: boolean; fromUserId?: string }
): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/${id}/resend`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Sending");
  }
};

export interface CaseStudy {
  id: string;
  name: string;
  headline: string | null;
  narrative: string | null;
  windowStart: string;
  windowEnd: string;
  states: string[] | null;
  stats: Record<string, number> | null;
  approved: boolean;
  lastVerifiedAt: string | null;
  driftNote: string | null;
}

/** Case studies. Only approved ones can go on a proposal. */
export const getCaseStudies = async (): Promise<
  { items: CaseStudy[] } | { error: string }
> => {
  try {
    const res = await axios.get(`/proposals/case-studies`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading case studies");
  }
};

export interface ProposalReporting {
  monthsBack: number;
  total: number;
  drafts: number;
  sent: number;
  opened: number;
  inFlight: number;
  coldOverAWeek: number;
  won: number;
  lost: number;
  decided: number;
  openRate: number | null;
  winRate: number | null;
  avgHoursToOpen: number | null;
  replies: number;
  handsUp: number;
  byType: Array<{
    customerType: CustomerType;
    total: number;
    opened: number;
    won: number;
    lost: number;
  }>;
}

export const getProposalReporting = async (
  monthsBack = 12
): Promise<ProposalReporting | { error: string }> => {
  try {
    const res = await axios.get(`/proposals/reporting/summary`, { params: { monthsBack } });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading reporting");
  }
};

/**
 * The document as the prospect sees it, rendered from the frozen payload and
 * served behind the normal company guard. The share link only resolves where
 * DNS points at the public host, which in practice is production only — this
 * is how an operator reads a sent proposal anywhere else.
 */
export const getProposalDocument = async (
  id: string
): Promise<{ html: string | null; reason: string | null } | { error: string }> => {
  try {
    const res = await axios.get(`/proposals/${id}/document`);
    return res?.data ?? { error: "No document returned." };
  } catch (e: any) {
    return fail(e, "Loading the document");
  }
};

/** Version chain, every view, and the feedback. The tracking view. */
export const getProposalHistory = async (
  id: string
): Promise<
  | {
      versions: Array<{
        id: string;
        version: number;
        status: string;
        sentAt: string | null;
        sentTo: string | null;
        viewCount: number;
        firstViewedAt: string | null;
        createdAt: string;
      }>;
      views: Array<{
        viewedAt: string;
        durationSec: number | null;
        deviceHash: string | null;
        lastTierIndex: number | null;
      }>;
      feedback: Array<{
        id: string;
        responses: Record<string, string>;
        followUpRequested: number;
        acknowledgedAt: string | null;
        submittedAt: string;
      }>;
    }
  | { error: string }
> => {
  try {
    const res = await axios.get(`/proposals/${id}/history`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading history");
  }
};

/** New draft from an existing proposal. Sites carry over; prices do not. */
export const regenerateProposal = async (id: string, senderUserId?: string | null): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/${id}/regenerate`, { senderUserId: senderUserId ?? null });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Regenerating");
  }
};

/** Won (the prospect accepted) or lost. */
export const setProposalOutcome = async (id: string, outcome: "won" | "lost", note?: string): Promise<any | { error: string }> => {
  try {
    const res = await axios.put(`/proposals/${id}/outcome`, { outcome, note });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving the outcome");
  }
};

/** Record that the link was sent outside the app (own email, Outbox). */
export const markProposalSent = async (id: string, sentTo: string): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/${id}/sent`, { sentTo, via: "manual" });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Recording the send");
  }
};

export interface ProposalSender {
  id: string;
  name: string;
  jobTitle: string | null;
  email: string;
  phone: string | null;
}

/** Staff users a proposal can be from. */
export const getProposalSenders = async (): Promise<ProposalSender[]> => {
  try {
    const res = await axios.get(`/proposals/senders`);
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
};

/** Every survey answer and contact-form reply across proposals, with counts. */
export const getAllProposalFeedback = async (
  proposalId?: string
): Promise<{ rows: any[]; counts: any } | { error: string }> => {
  try {
    const res = await axios.get(`/proposals/feedback`, { params: proposalId ? { proposalId } : {} });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading feedback");
  }
};

/** Soft-deletes. A sent proposal's share link stops working; a won one is refused. */
export const deleteProposal = async (
  id: string
): Promise<{ deleted: true; hadShareLink: boolean } | { error: string }> => {
  try {
    const res = await axios.delete(`/proposals/${id}`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Deleting");
  }
};

export interface EnrichedSite {
  locationId: string;
  endCustomerName: string | null;
  phone: string | null;
  website: string | null;
  email: string | null;
  ownerType: string | null;
  source: "places" | "contact_page" | "ai" | "manual" | null;
  confidence: "high" | "medium" | "low" | null;
  cached: boolean;
  resolvedAt: string | null;
}

export interface EnrichTarget {
  locationId: string;
  projectName?: string;
  latitude: number;
  longitude: number;
  ownerType?: string;
}

/** Cached answers. Free, so it runs without asking. */
export const getCachedContacts = async (
  locationIds: string[]
): Promise<{ sites: EnrichedSite[] } | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/enrich/cached`, { locationIds });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading contacts");
  }
};

/** What a lookup would cost, before committing to it. */
export const estimateEnrichment = async (
  targets: EnrichTarget[]
): Promise<
  | { total: number; cached: number; billable: number; estimatedUsd: number; remainingToday: number }
  | { error: string }
> => {
  try {
    const res = await axios.post(`/proposals/enrich/estimate`, { targets });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Estimating");
  }
};

export const enrichSites = async (
  targets: EnrichTarget[]
): Promise<
  | {
      sites: EnrichedSite[];
      billed: number;
      resolved: number;
      unresolved: number;
      capped: "per_call" | "per_day" | null;
      unavailable?: boolean;
    }
  | { error: string }
> => {
  try {
    const res = await axios.post(`/proposals/enrich`, { targets });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Finding contacts");
  }
};

export const setManualContact = async (
  locationId: string,
  input: { endCustomerName?: string; phone?: string; website?: string; email?: string }
): Promise<EnrichedSite | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/enrich/${locationId}/manual`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving contact");
  }
};

export interface ProposalListRow {
  id: string;
  name: string;
  prospectName: string;
  customerType: CustomerType;
  status: "draft" | "sent" | "viewed" | "won" | "lost";
  clientName: string | null;
  createdBy: string | null;
  sentAt: string | null;
  sentTo: string | null;
  viewCount: number;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  daysSinceSent: number | null;
  followUpRequested: number;
  pricedOnRepairedCoordinates: boolean;
  nextStep: string;
  updatedAt: string;
}

/**
 * TableData-compatible list fetch. Same call shape every other directory in
 * the app uses ({ query, controller }) so the shared table component handles
 * paging, sorting and search rather than this module reinventing them.
 */
export const getProposalsTable = async ({
  query = {},
  controller = null,
  excludeInterceptor = false,
}: any) => {
  try {
    const res: any = await axios.get(`/proposals`, {
      params: query,
      excludeInterceptor,
      ...(controller ? { signal: controller.signal } : {}),
    } as any);
    return res?.data ?? { items: [], total: 0, page: 1, pages: 0 };
  } catch {
    return { items: [], total: 0, page: 1, pages: 0 };
  }
};

/** The list. Rows state the next step rather than just a status. */
export const listProposals = async (params: {
  status?: string;
  search?: string;
  pageNum?: number;
  recordsPer?: number;
}): Promise<
  { items: ProposalListRow[]; total: number; page: number; pages: number } | { error: string }
> => {
  try {
    const res = await axios.get(`/proposals`, { params });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading proposals");
  }
};

/** Prospects who asked to be contacted and have not been. */
export const listFollowUps = async (): Promise<
  { items: any[]; total: number } | { error: string }
> => {
  try {
    const res = await axios.get(`/proposals/follow-ups`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading follow-ups");
  }
};

export const acknowledgeFollowUp = async (
  feedbackId: string
): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/follow-ups/${feedbackId}/acknowledge`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Acknowledging");
  }
};

/** Product + service options for step 1. Only pairs a depot can actually fulfil. */
export const getProposalCatalog = async (): Promise<
  | {
      products: Array<{ id: string; name: string; unit: string | null }>;
      services: Array<{ id: string; name: string; depots: number }>;
    }
  | { error: string }
> => {
  try {
    const res = await axios.get(`/proposals/catalog`);
    return res?.data ?? { error: "No catalog returned." };
  } catch (e: any) {
    return fail(e, "Loading catalog");
  }
};

/**
 * Resolve coordinates for rows that arrived without them. Capped per call and
 * per day server-side; `capped` says which limit stopped the run.
 */
export const geocodeRows = async (
  rows: ParsedRow[],
  batchSize?: number
): Promise<
  | {
      rows: ParsedRow[];
      attempted: number;
      resolved: number;
      failed: number;
      remainingToday: number;
      capped: "per_call" | "per_day" | null;
      unavailable?: boolean;
      resolvedFree?: number;
      /** Unresolved rows the server has not tried yet. */
      remaining?: number;
    }
  | { error: string }
> => {
  try {
    const res = await axios.post(`/proposals/geocode`, { rows, batchSize });
    return res?.data ?? { error: "Geocoding returned no data." };
  } catch (e: any) {
    return fail(e, "Geocoding");
  }
};

/** Parse a pasted list or CSV. Server-side, so flags match what pricing sees. */
export const parseLocationList = async (input: {
  text?: string;
  csv?: string;
}): Promise<{ rows: ParsedRow[]; summary: ParseSummary } | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/parse`, input);
    return res?.data ?? { error: "Parse returned no data." };
  } catch (e: any) {
    return fail(e, "Parse");
  }
};

/** An existing client's own sites, with coordinates and known yardage. */
export const getClientSites = async (
  clientId: string
): Promise<{ sites: SiteInput[]; total: number } | { error: string }> => {
  try {
    const res = await axios.get(`/proposals/sources/client/${clientId}`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading client sites");
  }
};

/** Completed builds, for the maintenance-plan pitch. */
export const getCompletedProjectSites = async (params: {
  minYards?: number;
  state?: string;
  monthsBack?: number;
  limit?: number;
}): Promise<{ sites: SiteInput[]; total: number } | { error: string }> => {
  try {
    const res = await axios.get(`/proposals/sources/completed-projects`, { params });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading completed projects");
  }
};

/** Price + assemble without saving. Safe to call on every toggle. */
export const previewProposal = async (
  input: BuildInput
): Promise<
  { payload: ProposalPayload; stats: any; warnings: PreviewWarnings } | { error: string }
> => {
  try {
    const res = await axios.post(`/proposals/preview`, input);
    return res?.data ?? { error: "Preview returned no data." };
  } catch (e: any) {
    return fail(e, "Preview");
  }
};

/** The document as the prospect would see it. */
export const previewProposalHtml = async (
  input: BuildInput
): Promise<{ html: string } | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/preview/html`, input);
    return res?.data ?? { error: "Preview returned no data." };
  } catch (e: any) {
    return fail(e, "Preview");
  }
};

export const createProposal = async (input: BuildInput): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/proposals`, input);
    return res?.data ?? { error: "Save returned no data." };
  } catch (e: any) {
    return fail(e, "Save");
  }
};

export const updateProposal = async (
  id: string,
  input: BuildInput
): Promise<any | { error: string }> => {
  try {
    const res = await axios.put(`/proposals/${id}`, input);
    return res?.data ?? { error: "Save returned no data." };
  } catch (e: any) {
    return fail(e, "Save");
  }
};

export const getProposal = async (id: string): Promise<any | { error: string }> => {
  try {
    const res = await axios.get(`/proposals/${id}`);
    return res?.data ?? { error: "Not found." };
  } catch (e: any) {
    return fail(e, "Loading proposal");
  }
};

/**
 * Freeze and share. Sends the payload the operator was looking at, so what
 * goes out is what was reviewed rather than a fresh reprice that could differ
 * from the preview they just approved.
 */
export const sendProposal = async (
  id: string,
  input: {
    payload: ProposalPayload;
    sentTo: string;
    cc?: string[];
    subject?: string;
    message?: string;
    abbrev?: string;
    includePassword?: boolean;
    skipEmail?: boolean;
    requirePassword?: boolean;
    password?: string;
  }
): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/${id}/send`, input);
    return res?.data ?? { error: "Send returned no data." };
  } catch (e: any) {
    return fail(e, "Send");
  }
};

/** Change (string) or remove (null) the password on a live link. */
export const setProposalPassword = async (
  id: string,
  password: string | null
): Promise<{ password: string | null } | { error: string }> => {
  try {
    const res = await axios.put(`/proposals/${id}/password`, { password });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Updating the password");
  }
};

/**
 * Attach a proposal (and, by default, every version of it) to a lead,
 * move it, or detach it with `leadId: null`. Leads module s.6.
 */
export const attachProposalToLead = async (
  id: string,
  leadId: string | null,
  allVersions = true,
): Promise<any | { error: string }> => {
  try {
    const res = await axios.put(`/proposals/${id}/lead`, { leadId, allVersions });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, leadId ? "Attaching to the lead" : "Detaching from the lead");
  }
};

/**
 * Step 3's opening section toggles, answered without pricing, so they are
 * right the moment the step opens instead of all-off until /preview returns.
 */
export const getSectionDefaults = async (input: { customerType: string; offerType?: string | null; siteCount: number }): Promise<{ offerType: string | null; sections: Record<string, boolean> | null } | null> => {
  try {
    const res = await axios.post(`/proposals/section-defaults`, input);
    return res?.data ?? null;
  } catch {
    return null;
  }
};

/**
 * The SAME proposal to another contact at the organization: a new version
 * on that contact's lead with the issued prices unchanged, then sent.
 */
export const sendProposalToContact = async (
  id: string,
  input: { leadId: string; sentTo: string; message?: string; subject?: string; requirePassword?: boolean; includePassword?: boolean; skipEmail?: boolean },
): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/proposals/${id}/send-to-contact`, input);
    return res?.data ?? { error: "Send returned no data." };
  } catch (e: any) {
    return fail(e, "Sending to the contact");
  }
};
